import { addDays, endOfMonth, endOfWeek, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import { CalendarDay, ReportDxData } from '../types';

/**
 * 캘린더 초기화 함수
 * : 선택된 월을 기준으로 주 단위로 구성된 캘린더 데이터를 생성한다.
 * @param selectedMonth: 선택된 월
 * @param today: 오늘 날짜
 * @returns 캘린더 데이터(ReportDxData)
 */
export const initCalendar = (selectedMonth: Date, today: Date): ReportDxData => {
  const monthStart = startOfMonth(selectedMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(endOfMonth(monthStart));

  const result: ReportDxData = [];
  let day = startDate;

  while (day <= endDate) {
    const week: CalendarDay[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(createCalendarDay(day, monthStart, today));
      day = addDays(day, 1);
    }
    result.push(week);
  }

  return result;
};

/**
 * 캘린더 날짜 객체 생성
 */
const createCalendarDay = (date: Date, monthStart: Date, today: Date): CalendarDay => ({
  checked: false,
  day: date.getDate(),
  dt: Math.floor(date.getTime() / 1000),
  isThisMonth: isSameMonth(date, monthStart),
  isToday: isSameDay(date, today),
  seekerCnt: 0,
  memberCnt: 0,
  teachCnt: 0,
  sermonCnt: 0,
  transportation: 0,
  activities: '',
});

/**
 * 제출된 dx 데이터를 캘린더 형식으로 변환
 * @param selectedMonth: 선택된 월
 * @param today: 오늘 날짜
 * @param submittedDxData: 제출된 dx 데이터 (visits, teaches, sermons, transportation, activities 포함)
 * @returns 캘린더 데이터(ReportDxData)
 */
export const mapSubmittedDxDataToCalendar = (
  selectedMonth: Date,
  today: Date,
  submittedDxData: {
    visits?: Array<{ dt: number; seekers_cnt?: number; members_cnt?: number }>;
    teaches?: Array<{ dt: number; cnt?: number }>;
    sermons?: Array<{ dt: number; cnt?: number }>;
    transportation?: Array<{ dt: number; cost?: number }>;
    activities?: Array<{ dt: number; content?: string }>;
  },
): ReportDxData => {
  const initCalender = initCalendar(selectedMonth, today);

  return initCalender.map((week) =>
    week.map((day) => ({
      ...day,
      seekerCnt: submittedDxData.visits?.find((v) => day.dt === v.dt)?.seekers_cnt || 0,
      memberCnt: submittedDxData.visits?.find((v) => day.dt === v.dt)?.members_cnt || 0,
      teachCnt: submittedDxData.teaches?.find((v) => day.dt === v.dt)?.cnt || 0,
      sermonCnt: submittedDxData.sermons?.find((v) => day.dt === v.dt)?.cnt || 0,
      transportation: submittedDxData.transportation?.find((v) => day.dt === v.dt)?.cost || 0,
      activities: submittedDxData.activities?.find((v) => day.dt === v.dt)?.content || '',
    })),
  );
};
